import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import usePresentPage from 'client/hooks/usePresentPage'
import {
  getGlobalMobileTextFontSize,
  getGlobalMobileTextLineHeight,
  getGlobalTextFontSize,
  getGlobalTextLineHeight,
} from 'client/reducers/pageReducer'
import PropTypes from 'prop-types'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalculateTypography } from 'src/editor/shared/hooks/use-calculate-typography'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import MobileIconUi from '../styles/ui/MobileIconUi'
import ResetIcon from './ui/ResetIcon'

const browserDefaultLineHeight = 1.3

/**
 * Component for controlling text line height with responsive design support
 *
 * @param {Object} props - Component props
 * @param {(string|number)} [props.fontSize] - The font size value for desktop mode
 * @param {(string|number)} [props.mobileFontSize] - The font size value for mobile mode
 * @param {(string|number)} [props.lineHeight=null] - The line height value for desktop mode
 * @param {(string)} [props.typographyType] - The typography type (e.g., 'heading', 'paragraph') to calculate correct global fonts
 * @param {(string|number)} [props.mobileLineHeight=null] - The line height value for mobile mode
 * @param {Function} props.update - Function to update the line height in desktop mode
 * @param {Function} props.mobileUpdate - Function to update the line height in mobile mode
 * @param {Function} [props.removeEntityMobileStyle] - Function to reset mobile line height to global default
 * @param {Function} [props.removeEntityStyle] - Function to reset desktop line height to global default
 * @returns {JSX.Element} Range component with line height control
 */
function TextLineHeight({
  fontSize,
  mobileFontSize,
  lineHeight = null,
  mobileLineHeight = null,
  typographyType,
  ...rest
}) {
  const { t } = useTranslation()
  const { isMobile } = useDeviceModeContext()
  const {
    globalTextFontSize,
    globalMobileFontSize,
    globalLineHeight,
    globalMobileLineHeight,
  } = useCalculateTypography({ typographyType })

  // global text size always exists
  const currentGlobalFontSizeParsed = parseInt(
    isMobile ? globalMobileFontSize : globalTextFontSize,
    0,
  )

  // global line height could be undefined
  const currentGlobalLineHeightPx = isMobile
    ? globalMobileLineHeight
    : globalLineHeight
  const currentGlobalLineHeightParsed = currentGlobalLineHeightPx
    ? currentGlobalLineHeightPx
    : null

  const currentLineHeightPx = isMobile ? mobileLineHeight : lineHeight
  const currentLineHeightParsed = currentLineHeightPx
    ? currentLineHeightPx
    : null

  const currentFontSizePx = isMobile ? mobileFontSize : fontSize
  const currentFontSizeParsed = currentFontSizePx ? currentFontSizePx : null

  const calculatedFontSize =
    currentFontSizeParsed || currentGlobalFontSizeParsed
  const computedLineHeight = Math.round(
    browserDefaultLineHeight * calculatedFontSize,
  )

  let calculatedLineHeight
  if (!currentLineHeightPx) {
    calculatedLineHeight = currentGlobalLineHeightParsed || computedLineHeight
  } else {
    calculatedLineHeight = currentLineHeightParsed
  }

  const isResetAllowed =
    currentLineHeightParsed !== undefined && currentLineHeightParsed !== null

  const reset = () =>
    isMobile ? rest.removeEntityMobileStyle() : rest.removeEntityStyle()

  const update = value => {
    if (value < 8) {
      return
    }

    if (isMobile) {
      rest.mobileUpdate(value)
    } else {
      rest.update(value)
    }
  }

  return (
    <RangeElement
      label="settings_styles.line_height.label"
      labelIcon={
        <React.Fragment>
          {isMobile && <MobileIconUi />}
          {isResetAllowed && (
            <ResetIcon
              onClick={reset}
              title={t('settings_styles.line_height.inherit')}
            />
          )}
        </React.Fragment>
      }
      value={calculatedLineHeight}
      onChange={update}
      min={8}
      isDecimalAllowed
    />
  )
}

TextLineHeight.propTypes = {
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mobileLineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mobileFontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  update: PropTypes.func.isRequired,
  mobileUpdate: PropTypes.func.isRequired,
  removeEntityMobileStyle: PropTypes.func.isRequired,
  removeEntityStyle: PropTypes.func.isRequired,
  typographyType: PropTypes.string,
}

export default memo(TextLineHeight)
